import * as THREE from 'three'

export type StructureType =
  | 'castle'
  | 'house'
  | 'trading_post'
  | 'stable'
  | 'pasture'
  | 'church'
  | 'pub'
  | 'armory'
  | 'barn'
  | 'dock'
  | 'farm'

export type PropType =
  | 'well'
  | 'barrel'
  | 'woodpile'
  | 'haystack'
  | 'cart'
  | 'crate'
  | 'trough'

export interface Structure {
  type: StructureType
  localX: number
  localZ: number
  rotation: number // radians around up axis
}

export interface Prop {
  type: PropType
  localX: number
  localZ: number
  rotation: number
}

export interface PathSegment {
  fromX: number
  fromZ: number
  toX: number
  toZ: number
}

export interface FencePost {
  localX: number
  localZ: number
}

export interface VillageLayout {
  structures: Structure[]
  paths: PathSegment[]
  fencePosts: FencePost[]
  fenceRadius: number
  props: Prop[]
}

export interface VillagePlacement {
  normal: THREE.Vector3
  position: THREE.Vector3
  tangentX: THREE.Vector3
  tangentZ: THREE.Vector3
  layout: VillageLayout
  seed: number
  size: 'small' | 'medium' | 'large'
}

export const VILLAGE_SCALE = 1

// Structural info for each building type
// [width, ridgeHeight, depth, wallHeight, roofPitchDeg]
export interface StructureInfo {
  width: number
  ridgeHeight: number
  depth: number
  wallHeight: number
  roofPitch: number // degrees
  wallColor: string
  roofColor: string
  trimColor: string // timber frame / trim
}

export const STRUCTURE_INFO: Record<StructureType, StructureInfo> = {
  // Longhouse proportions: narrow, long, "mostly roof"
  house: {
    width: 5.5, ridgeHeight: 5.5, depth: 13, wallHeight: 2.2,
    roofPitch: 52, wallColor: '#F2DDC2', roofColor: '#C4A050', trimColor: '#3F2D20',
  },
  // Castle keep with curtain wall compound
  castle: {
    width: 22, ridgeHeight: 24, depth: 22, wallHeight: 20,
    roofPitch: 25, wallColor: '#8B8B83', roofColor: '#696969', trimColor: '#5C5C54',
  },
  // Long nave + west tower
  church: {
    width: 7, ridgeHeight: 11, depth: 24, wallHeight: 6,
    roofPitch: 55, wallColor: '#D4C5A9', roofColor: '#6B6B63', trimColor: '#8B7355',
  },
  // Market hall: open ground floor with pillars, upper storey
  trading_post: {
    width: 10, ridgeHeight: 8, depth: 12, wallHeight: 4,
    roofPitch: 48, wallColor: '#D9A96B', roofColor: '#877056', trimColor: '#3F2D20',
  },
  // Long, low, large door
  stable: {
    width: 6, ridgeHeight: 5, depth: 12, wallHeight: 2.8,
    roofPitch: 42, wallColor: '#8B6B4A', roofColor: '#877056', trimColor: '#5C3A1E',
  },
  // Enclosed paddock (fence + ground only)
  pasture: {
    width: 45, ridgeHeight: 1.5, depth: 35, wallHeight: 1.2,
    roofPitch: 0, wallColor: '#556B2F', roofColor: '#556B2F', trimColor: '#8B6914',
  },
  // Tavern: like house but wider, jettied upper floor
  pub: {
    width: 7, ridgeHeight: 7, depth: 10, wallHeight: 4.5,
    roofPitch: 50, wallColor: '#E0D4C1', roofColor: '#877056', trimColor: '#3F2D20',
  },
  // Smithy: open front, prominent chimney
  armory: {
    width: 7, ridgeHeight: 5.5, depth: 8, wallHeight: 3.2,
    roofPitch: 40, wallColor: '#696969', roofColor: '#5C4736', trimColor: '#2B241F',
  },
  // Large barn: tall wagon doors, steep roof
  barn: {
    width: 8, ridgeHeight: 8, depth: 16, wallHeight: 3.5,
    roofPitch: 52, wallColor: '#8B6B4A', roofColor: '#877056', trimColor: '#5C3A1E',
  },
  // Dock house: small hut on the shoreline with a pier extending into water
  dock: {
    width: 5, ridgeHeight: 4, depth: 6, wallHeight: 2.4,
    roofPitch: 45, wallColor: '#9E8B70', roofColor: '#6B5D4F', trimColor: '#3F2D20',
  },
  // Farmstead: small house with a large fenced plot of tilled land
  farm: {
    width: 5, ridgeHeight: 4.5, depth: 8, wallHeight: 2.0,
    roofPitch: 48, wallColor: '#D9C4A0', roofColor: '#877056', trimColor: '#4A3622',
  },
}

// Legacy compat: [width, ridgeHeight, depth]
export const STRUCTURE_DIMS: Record<StructureType, [number, number, number]> = Object.fromEntries(
  Object.entries(STRUCTURE_INFO).map(([k, v]) => [k, [v.width, v.ridgeHeight, v.depth]])
) as Record<StructureType, [number, number, number]>

export const STRUCTURE_COLORS: Record<StructureType, string> = Object.fromEntries(
  Object.entries(STRUCTURE_INFO).map(([k, v]) => [k, v.wallColor])
) as Record<StructureType, string>
